import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createPaymentPlan,
  deletePaymentPlan,
  fetchPaymentPlan,
  fetchPaymentPlans,
  markPlanInstallmentsPaid,
  updatePaymentPlan,
  type CreatePlanDraft,
  type MarkPlanPaidDraft,
  type PaymentPlanDetail,
  type PaymentPlanListItem,
  type PlanDeleteResult,
  type PlanMarkPaidResult,
  type PlanRequestOptions,
  type PlanWriteResult,
  type UpdatePlanDraft,
} from '@/lib/paymentPlans';

/**
 * Query layer for Maksekokkulepped (replit.md §1.4 — all state flows through one
 * query layer with shared keys). Every write re-materializes plain
 * cash_flow_entry rows on the server, so each mutation invalidates THREE shared
 * trees: its own roster, the `cash-flow` tree (both lenses + register + search
 * pick up the changed rows) and the shared `freshness` key (the write stamps the
 * `payment_plan` sync_state row).
 */
export const paymentPlansQueryKey = ['payment-plans'] as const;

/** Shared key for one plan's live detail (schedule + covered invoices). */
export function paymentPlanQueryKey(planId: string) {
  return ['payment-plans', planId] as const;
}

export interface UsePaymentPlansResult {
  plans: PaymentPlanListItem[];
  isLoading: boolean;
  isError: boolean;
}

export function usePaymentPlans(options?: PlanRequestOptions): UsePaymentPlansResult {
  const query = useQuery({
    queryKey: paymentPlansQueryKey,
    queryFn: () => fetchPaymentPlans(options),
  });
  return { plans: query.data ?? [], isLoading: query.isLoading, isError: query.isError };
}

export interface UsePaymentPlanResult {
  detail: PaymentPlanDetail | null;
  isLoading: boolean;
  isError: boolean;
}

/** One plan's detail — `planId: null` keeps the query idle (closed pane). */
export function usePaymentPlan(planId: string | null, options?: PlanRequestOptions): UsePaymentPlanResult {
  const query = useQuery({
    queryKey: paymentPlanQueryKey(planId ?? 'none'),
    queryFn: () => fetchPaymentPlan(planId as string, options),
    enabled: planId !== null,
  });
  return { detail: query.data ?? null, isLoading: query.isLoading, isError: query.isError };
}

function useInvalidateAfterMaterialize() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: paymentPlansQueryKey });
    queryClient.invalidateQueries({ queryKey: ['cash-flow'] });
    queryClient.invalidateQueries({ queryKey: ['freshness'] });
  };
}

export function useCreatePaymentPlan(options?: PlanRequestOptions) {
  const invalidate = useInvalidateAfterMaterialize();
  return useMutation<PlanWriteResult, Error, CreatePlanDraft>({
    mutationFn: (draft) => createPaymentPlan(draft, options),
    onSuccess: invalidate,
  });
}

export function useUpdatePaymentPlan(options?: PlanRequestOptions) {
  const invalidate = useInvalidateAfterMaterialize();
  return useMutation<PlanWriteResult, Error, { id: string; draft: UpdatePlanDraft }>({
    mutationFn: ({ id, draft }) => updatePaymentPlan(id, draft, options),
    onSuccess: invalidate,
  });
}

export function useDeletePaymentPlan(options?: PlanRequestOptions) {
  const invalidate = useInvalidateAfterMaterialize();
  return useMutation<PlanDeleteResult, Error, string>({
    mutationFn: (id) => deletePaymentPlan(id, options),
    onSuccess: invalidate,
  });
}

/** Mark-paid settles calendar rows server-side, so it invalidates the same three
 *  shared trees PLUS the plan's own detail (paymentPlansQueryKey is its prefix). */
export function useMarkPlanInstallmentsPaid(planId: string, options?: PlanRequestOptions) {
  const queryClient = useQueryClient();
  return useMutation<PlanMarkPaidResult, Error, MarkPlanPaidDraft>({
    mutationFn: (draft) => markPlanInstallmentsPaid(planId, draft, options),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentPlansQueryKey });
      queryClient.invalidateQueries({ queryKey: ['cash-flow'] });
      queryClient.invalidateQueries({ queryKey: ['freshness'] });
    },
  });
}
